// client/src/features/quotations/answerCatalogueVatPrompt.ts
//
// Answering "Does this item have VAT?" (`CatalogueVatPrompt.tsx`) — as an
// orchestration function, DEPENDENCY-INJECTED for its network calls, so this
// can be unit-tested against mocked API responses without rendering
// `QuotationDetailPage` (a god file under a line ceiling, TECH_DEBT #26).
// `QuotationDetailPage.handleCatalogueVatAnswer` is a thin wrapper around
// this that supplies the real API functions and the page's `setVatPrompt`
// side effect.
//
// ONE re-pricing shape across all three VAT-answer entry points now — this
// used to be the ONE HOLDOUT still going through `deps.refreshLinePrice`, the
// page's per-line Refresh (↻): a NAME-based `lookupItemPrice` re-search that
// can land on a DIFFERENT supplier/row than the one the badge was actually
// asking about (the same defect already fixed for a same-session catalogue
// pick, `pendingCataloguePickVat.ts`, and for a supplier-dropdown pick,
// `pendingSupplierVatPick.ts` — audit finding, 2026-09-25).
//
// The line's own "VAT?" badge (`pricingDetails.catalogueVatUnset === true`)
// DOES carry an identifying fact in memory even though the row itself is
// not held: `pricingDetails.masterCatalogueId`, the exact row that priced
// the line (`QuotationDetailPage`'s badge `onClick` reads it off the same
// field `CatalogueOptionsLink`'s highlight and `pricedFromIdentity` already
// trust for this). So this now RE-FETCHES that row BY ID (the same typed
// single-item GET the other two entry points re-fetch through), prices it at
// the line's CURRENT unit (`priceAtUnit` — never a fresh name search that
// could re-resolve to a different product), and writes through
// `applyCataloguePick` — the SAME write any catalogue pick uses, so the user
// sees the same before -> after confirm rather than a silent re-price.

import type { MasterCatalogueItem } from '../../lib/masterCatalogueApi';
import { priceAtUnit, type CataloguePriceSelection } from './itemLookupRows';
import { costVatAgreesWithLoadedRate, vatRateChangedError } from './cataloguePickDetails';

export interface AnswerCatalogueVatPromptDeps {
  setCatalogueVat: (id: string, vatApplicable: boolean) => Promise<unknown>;
  /** The existing typed single-item fetch (`masterCatalogueApi.getMasterCatalogueItem`) — never a new fetch shape. */
  getCatalogueItem: (id: string) => Promise<{ success: boolean; data: MasterCatalogueItem }>;
  /** The ONE write any catalogue pick uses — shows the before -> after confirm. */
  applyCataloguePick: (
    index: number,
    item: MasterCatalogueItem,
    priceSelection?: CataloguePriceSelection
  ) => Promise<void>;
}

export interface AnswerCatalogueVatPromptArgs {
  index: number;
  masterCatalogueId: string;
  vatApplicable: boolean;
  /** The line's CURRENT unit — the row is re-priced at THIS unit, never a fresh name search. */
  lineUom: string;
  /** TT$ per US$1 — the quotation's own locked rate, or null. Same convention `priceAtUnit` takes. */
  quotationUsdToTtd: number | null;
  /** The Settings VAT rate (`getVatRate`), percent, or null. */
  vatRatePercent: number | null;
}

/**
 * The ONE writer of a row's `vat_applicable` from this popup, saved before
 * the re-fetch so the catalogue row states the answer even if the re-price
 * below fails, then re-fetch-by-id, price-at-the-line's-unit, and write.
 *
 * @throws when the re-fetch comes back with no row, or when the answered row
 *   cannot be priced at the line's own unit (e.g. a whole-number conversion
 *   the row's packaging cannot honestly restate). Refuse, never guess
 *   (invariant 8): nothing is written to the line in either case, and
 *   `err.message` is the same refusal wording `priceAtUnit` already gives
 *   the lookup panel.
 */
export async function answerCatalogueVatPrompt(
  args: AnswerCatalogueVatPromptArgs,
  deps: AnswerCatalogueVatPromptDeps
): Promise<void> {
  const { index, masterCatalogueId, vatApplicable, lineUom, quotationUsdToTtd, vatRatePercent } = args;
  await deps.setCatalogueVat(masterCatalogueId, vatApplicable);

  const fetched = await deps.getCatalogueItem(masterCatalogueId);
  if (!fetched?.success || !fetched.data) {
    throw new Error('The Master Catalogue did not return this row after the VAT answer was saved.');
  }
  const answeredItem: MasterCatalogueItem = { ...fetched.data, vat_applicable: vatApplicable };
  // The re-fetched row's fact is the rate the server read serving it; the cost
  // below is grossed at the rate this page loaded. Refused when they differ
  // (`cataloguePickDetails.costVatAgreesWithLoadedRate`) — the answer itself
  // is already saved on the row.
  if (!costVatAgreesWithLoadedRate(answeredItem, vatRatePercent)) {
    throw vatRateChangedError();
  }

  const priced = priceAtUnit(
    { kind: 'catalogue', id: masterCatalogueId, item: answeredItem },
    { unit: lineUom, quotationUsdToTtd, vatRatePercent }
  );
  if (!priced.ok) {
    throw new Error(priced.reason);
  }

  await deps.applyCataloguePick(index, answeredItem, priced.catalogueSelection);
}
